import { getSupabaseBrowserClient } from "@/lib/supabase/client";
import {
  ADMIN_POST_TYPES,
  type AccountStatus,
  type AdminActivityPoint,
  type AdminCommunityRow,
  type AdminDashboardStats,
  type AdminNotificationBroadcast,
  type AdminPostAction,
  type AdminPostAudience,
  type AdminPostRow,
  type AdminPostType,
  type AdminReportRow,
  type AdminAuditLogRow,
  type AdminConversationMessage,
  type AdminConversationRow,
  type AdminConversationType,
  type AdminMessageAction,
  type AdminMessageType,
  type AdminServiceCategoryOption,
  type AdminServiceCategoryUpsertInput,
  type AdminServiceDetail,
  type AdminServiceDocument,
  type AdminServiceOptions,
  type AdminServiceRow,
  type AdminServiceUpsertInput,
  type AdminUserRow,
  type ReportReason,
  type ReportStatus,
  type ReportTargetType,
  type ServiceAdminStatus,
  type ServiceCtaType,
  type ServiceFulfillmentType,
  type ServicePricingModel,
  type FeedAccessDuration,
  type SystemRole,
} from "@/lib/admin/platform-types";
import { signServiceMedia } from "@/lib/services/api";

function asNumber(value: unknown) {
  const parsed = Number(value ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
}

function asRows<T>(data: unknown): T[] {
  return Array.isArray(data) ? (data as T[]) : [];
}

export async function getAdminDashboard(): Promise<AdminDashboardStats> {
  const supabase = getSupabaseBrowserClient();
  const { data, error } = await supabase.rpc("get_admin_dashboard");

  if (error) {
    throw error;
  }

  const payload = (data ?? {}) as {
    community?: Record<string, unknown>;
    business?: Record<string, unknown> & {
      leadsByService?: { name?: string; count?: number }[];
    };
  };
  const community = payload.community ?? {};
  const business = payload.business ?? {};

  return {
    community: {
      registeredUsers: asNumber(community.registeredUsers),
      activeUsers: asNumber(community.activeUsers),
      pendingUsers: asNumber(community.pendingUsers),
      dau: asNumber(community.dau),
      wau: asNumber(community.wau),
      mau: asNumber(community.mau),
      postsToday: asNumber(community.postsToday),
      postsTotal: asNumber(community.postsTotal),
      commentsToday: asNumber(community.commentsToday),
      commentsTotal: asNumber(community.commentsTotal),
      reactionsTotal: asNumber(community.reactionsTotal),
      communitiesTotal: asNumber(community.communitiesTotal),
      communitiesActive: asNumber(community.communitiesActive),
      openReports: asNumber(community.openReports),
    },
    business: {
      leadsTotal: asNumber(business.leadsTotal),
      leadsNew: asNumber(business.leadsNew),
      leadsActive: asNumber(business.leadsActive),
      offersTotal: asNumber(business.offersTotal),
      offersAccepted: asNumber(business.offersAccepted),
      contracted: asNumber(business.contracted),
      expectedValue: asNumber(business.expectedValue),
      conversionRate: asNumber(business.conversionRate),
      leadsByService: Array.isArray(business.leadsByService)
        ? business.leadsByService.map((item) => ({
            name: item.name ?? "Szolgáltatás",
            count: asNumber(item.count),
          }))
        : [],
    },
  };
}

export async function getAdminActivitySeries(
  days = 14,
): Promise<AdminActivityPoint[]> {
  const supabase = getSupabaseBrowserClient();
  const { data, error } = await supabase.rpc("get_admin_activity_series", {
    p_days: days,
  });

  if (error) {
    throw error;
  }

  return asRows<{
    date?: string;
    posts?: number;
    comments?: number;
    reactions?: number;
    messages?: number;
    registrations?: number;
    total?: number;
  }>(data).map((row) => ({
    date: typeof row.date === "string" ? row.date : "",
    posts: asNumber(row.posts),
    comments: asNumber(row.comments),
    reactions: asNumber(row.reactions),
    messages: asNumber(row.messages),
    registrations: asNumber(row.registrations),
    total: asNumber(row.total),
  }));
}

export async function listAdminUsers(): Promise<AdminUserRow[]> {
  const supabase = getSupabaseBrowserClient();
  const { data, error } = await supabase.rpc("list_admin_users");

  if (error) {
    throw error;
  }

  return asRows<{
    id: string;
    full_name: string;
    email: string | null;
    occupation: string | null;
    city: string | null;
    account_status: AccountStatus;
    system_role: string | null;
    last_seen_at: string | null;
    created_at: string;
    community_count: number;
    request_count: number;
  }>(data).map((row) => ({
    id: row.id,
    fullName: row.full_name,
    email: row.email,
    occupation: row.occupation,
    city: row.city,
    accountStatus: row.account_status,
    systemRole: row.system_role,
    lastSeenAt: row.last_seen_at,
    createdAt: row.created_at,
    communityCount: asNumber(row.community_count),
    requestCount: asNumber(row.request_count),
  }));
}

export async function setAdminAccountStatus(
  userId: string,
  status: AccountStatus,
) {
  const supabase = getSupabaseBrowserClient();
  const { error } = await supabase.rpc("admin_set_account_status", {
    target_user_id: userId,
    next_status: status,
  });

  if (error) {
    throw error;
  }
}

export async function setAdminSystemRole(
  userId: string,
  role: SystemRole | null,
) {
  const supabase = getSupabaseBrowserClient();
  const { error } = await supabase.rpc("admin_set_system_role", {
    target_user_id: userId,
    next_role: role,
  });

  if (error) {
    throw error;
  }
}

function asAdminPostType(value: string): AdminPostType {
  return ADMIN_POST_TYPES.includes(value as AdminPostType)
    ? (value as AdminPostType)
    : "text";
}

function asAdminPostAudience(value: string): AdminPostAudience {
  if (value === "community" || value === "practice") {
    return value;
  }
  return "global";
}

export async function listAdminPosts(): Promise<AdminPostRow[]> {
  const supabase = getSupabaseBrowserClient();
  const { data, error } = await supabase.rpc("list_admin_posts");

  if (error) {
    throw error;
  }

  return asRows<{
    id: string;
    author_id: string;
    author_name: string;
    post_type: string;
    audience: string;
    content_preview: string;
    community_names: string | null;
    practice_names: string | null;
    is_hidden: boolean;
    is_pinned: boolean;
    deleted_at: string | null;
    comment_count: number;
    created_at: string;
  }>(data).map((row) => ({
    id: row.id,
    authorId: row.author_id,
    authorName: row.author_name,
    postType: asAdminPostType(row.post_type),
    audience: asAdminPostAudience(row.audience),
    contentPreview: row.content_preview,
    communityNames: row.community_names,
    practiceNames: row.practice_names,
    isHidden: Boolean(row.is_hidden),
    isPinned: Boolean(row.is_pinned),
    deletedAt: row.deleted_at,
    commentCount: asNumber(row.comment_count),
    createdAt: row.created_at,
  }));
}

export async function moderateAdminPost(
  postId: string,
  action: AdminPostAction,
) {
  const supabase = getSupabaseBrowserClient();
  const { error } = await supabase.rpc("admin_moderate_post", {
    target_post_id: postId,
    post_action: action,
  });

  if (error) {
    throw error;
  }
}

function asConversationType(value: string): AdminConversationType {
  if (value === "group" || value === "community") {
    return value;
  }
  return "direct";
}

function asMessageType(value: string): AdminMessageType {
  if (
    value === "image" ||
    value === "file" ||
    value === "link" ||
    value === "system"
  ) {
    return value;
  }
  return "text";
}

function mapAdminConversation(row: {
  id: string;
  conversation_type: string;
  title: string | null;
  community_name: string | null;
  allows_video: boolean;
  archived_at: string | null;
  member_count: number;
  member_names: string | null;
  message_count: number;
  last_message_preview: string | null;
  last_message_at: string | null;
  created_at: string;
  updated_at: string;
}): AdminConversationRow {
  return {
    id: row.id,
    conversationType: asConversationType(row.conversation_type),
    title: row.title,
    communityName: row.community_name,
    allowsVideo: Boolean(row.allows_video),
    archivedAt: row.archived_at,
    memberCount: asNumber(row.member_count),
    memberNames: row.member_names,
    messageCount: asNumber(row.message_count),
    lastMessagePreview: row.last_message_preview,
    lastMessageAt: row.last_message_at,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

export async function listAdminConversations(): Promise<AdminConversationRow[]> {
  const supabase = getSupabaseBrowserClient();
  const { data, error } = await supabase.rpc("list_admin_conversations");

  if (error) {
    throw error;
  }

  return asRows<{
    id: string;
    conversation_type: string;
    title: string | null;
    community_name: string | null;
    allows_video: boolean;
    archived_at: string | null;
    member_count: number;
    member_names: string | null;
    message_count: number;
    last_message_preview: string | null;
    last_message_at: string | null;
    created_at: string;
    updated_at: string;
  }>(data).map(mapAdminConversation);
}

export async function getAdminConversation(
  conversationId: string,
): Promise<AdminConversationRow> {
  const supabase = getSupabaseBrowserClient();
  const { data, error } = await supabase.rpc("get_admin_conversation", {
    target_conversation_id: conversationId,
  });

  if (error) {
    throw error;
  }

  const row = asRows<{
    id: string;
    conversation_type: string;
    title: string | null;
    community_name: string | null;
    allows_video: boolean;
    archived_at: string | null;
    member_count: number;
    member_names: string | null;
    message_count: number;
    last_message_preview: string | null;
    last_message_at: string | null;
    created_at: string;
    updated_at: string;
  }>(data)[0];

  if (!row) {
    throw new Error("A beszélgetés nem található");
  }

  return mapAdminConversation(row);
}

export async function listAdminConversationMessages(
  conversationId: string,
): Promise<AdminConversationMessage[]> {
  const supabase = getSupabaseBrowserClient();
  const { data, error } = await supabase.rpc(
    "list_admin_conversation_messages",
    { target_conversation_id: conversationId },
  );

  if (error) {
    throw error;
  }

  return asRows<{
    id: string;
    sender_id: string;
    sender_name: string;
    message_type: string;
    body: string | null;
    created_at: string;
    edited_at: string | null;
    deleted_at: string | null;
  }>(data)
    .map((row) => ({
      id: row.id,
      senderId: row.sender_id,
      senderName: row.sender_name,
      messageType: asMessageType(row.message_type),
      body: row.body,
      createdAt: row.created_at,
      editedAt: row.edited_at,
      deletedAt: row.deleted_at,
    }))
    .reverse();
}

export async function moderateAdminMessage(
  messageId: string,
  action: AdminMessageAction,
) {
  const supabase = getSupabaseBrowserClient();
  const { error } = await supabase.rpc("admin_moderate_message", {
    target_message_id: messageId,
    message_action: action,
  });

  if (error) {
    throw error;
  }
}

export async function setAdminConversationArchived(
  conversationId: string,
  archived: boolean,
) {
  const supabase = getSupabaseBrowserClient();
  const { error } = await supabase.rpc("admin_set_conversation_archived", {
    target_conversation_id: conversationId,
    next_archived: archived,
  });

  if (error) {
    throw error;
  }
}

export async function listAdminAuditLogs(): Promise<AdminAuditLogRow[]> {
  const supabase = getSupabaseBrowserClient();
  const { data, error } = await supabase.rpc("list_admin_audit_logs");

  if (error) {
    throw error;
  }

  return asRows<{
    id: string;
    created_at: string;
    actor_name: string | null;
    action: string;
    entity_type: string;
    entity_id: string | null;
    metadata: unknown;
  }>(data).map((row) => ({
    id: row.id,
    createdAt: row.created_at,
    actorName: row.actor_name,
    action: row.action,
    entityType: row.entity_type,
    entityId: row.entity_id,
    metadata:
      row.metadata && typeof row.metadata === "object" && !Array.isArray(row.metadata)
        ? (row.metadata as Record<string, unknown>)
        : {},
  }));
}

export async function createAdminSystemNotifications(input: {
  type: "system_announcement" | "system_message";
  title: string;
  body: string | null;
  targetPath: string;
  recipientIds: string[] | null;
}) {
  const supabase = getSupabaseBrowserClient();
  const { data, error } = await supabase.rpc("create_system_notifications", {
    p_type: input.type,
    p_title: input.title,
    p_body: input.body,
    p_target_path: input.targetPath,
    p_recipient_ids: input.recipientIds,
  });

  if (error) {
    throw error;
  }

  return asNumber(data);
}

export async function listAdminNotificationBroadcasts(): Promise<
  AdminNotificationBroadcast[]
> {
  const supabase = getSupabaseBrowserClient();
  const { data, error } = await supabase.rpc(
    "list_admin_notification_broadcasts",
  );

  if (error) {
    throw error;
  }

  return asRows<{
    id: string;
    created_at: string;
    actor_name: string | null;
    notification_type: string;
    title: string;
    body: string | null;
    target_path: string;
    broadcast: boolean;
    recipient_count: number;
  }>(data).map((row) => ({
    id: row.id,
    createdAt: row.created_at,
    actorName: row.actor_name,
    notificationType:
      row.notification_type === "system_message"
        ? "system_message"
        : "system_announcement",
    title: row.title,
    body: row.body,
    targetPath: row.target_path,
    broadcast: Boolean(row.broadcast),
    recipientCount: asNumber(row.recipient_count),
  }));
}

export async function listAdminCommunities(): Promise<AdminCommunityRow[]> {
  const supabase = getSupabaseBrowserClient();
  const { data, error } = await supabase.rpc("list_admin_communities");

  if (error) {
    throw error;
  }

  return asRows<{
    id: string;
    name: string;
    slug: string;
    visibility: string;
    owner_name: string;
    member_count: number;
    is_featured: boolean;
    is_active: boolean;
    created_at: string;
    suspended_at: string | null;
    archived_at: string | null;
  }>(data).map((row) => ({
    id: row.id,
    name: row.name,
    slug: row.slug,
    visibility: row.visibility,
    ownerName: row.owner_name,
    memberCount: asNumber(row.member_count),
    isFeatured: Boolean(row.is_featured),
    isActive: Boolean(row.is_active),
    createdAt: row.created_at,
    suspendedAt: row.suspended_at,
    archivedAt: row.archived_at,
  }));
}

export async function setAdminCommunityState(
  communityId: string,
  input: {
    featured?: boolean | null;
    suspended?: boolean | null;
    archived?: boolean | null;
  },
) {
  const supabase = getSupabaseBrowserClient();
  const { error } = await supabase.rpc("admin_set_community_state", {
    target_community_id: communityId,
    next_featured: input.featured ?? null,
    next_suspended: input.suspended ?? null,
    next_archived: input.archived ?? null,
  });

  if (error) {
    throw error;
  }
}

export async function listAdminServices(): Promise<AdminServiceRow[]> {
  const supabase = getSupabaseBrowserClient();
  const { data, error } = await supabase.rpc("list_admin_services");

  if (error) {
    throw error;
  }

  return asRows<{
    id: string;
    name: string;
    slug: string;
    category_name: string;
    status: ServiceAdminStatus;
    published_at: string | null;
    lead_count: number;
    updated_at: string;
    partner_name: string | null;
    is_featured: boolean;
    sort_order: number;
    cta_type: ServiceCtaType;
    pricing_model: ServicePricingModel;
    price_amount: number | string | null;
    price_unit: string | null;
  }>(data).map((row) => ({
    id: row.id,
    name: row.name,
    slug: row.slug,
    categoryName: row.category_name,
    status: row.status,
    publishedAt: row.published_at,
    leadCount: asNumber(row.lead_count),
    updatedAt: row.updated_at,
    partnerName: row.partner_name,
    isFeatured: Boolean(row.is_featured),
    sortOrder: asNumber(row.sort_order),
    ctaType: row.cta_type,
    pricingModel: row.pricing_model,
    priceAmount:
      row.price_amount === null || row.price_amount === undefined
        ? null
        : asNumber(row.price_amount),
    priceUnit: typeof row.price_unit === "string" ? row.price_unit : null,
  }));
}

export async function setAdminServiceStatus(
  serviceId: string,
  status: ServiceAdminStatus,
) {
  const supabase = getSupabaseBrowserClient();
  const { error } = await supabase.rpc("admin_set_service_status", {
    target_service_id: serviceId,
    next_status: status,
  });

  if (error) {
    throw error;
  }
}

export async function listAdminServiceOptions(): Promise<AdminServiceOptions> {
  const supabase = getSupabaseBrowserClient();
  const { data, error } = await supabase.rpc("list_admin_service_options");

  if (error) {
    throw error;
  }

  const payload = (data ?? {}) as {
    categories?: AdminServiceCategoryOption[];
  };

  return {
    categories: Array.isArray(payload.categories) ? payload.categories : [],
  };
}

export async function getAdminService(
  serviceId: string,
): Promise<AdminServiceDetail> {
  const supabase = getSupabaseBrowserClient();
  const { data, error } = await supabase.rpc("get_admin_service", {
    target_service_id: serviceId,
  });

  if (error) {
    throw error;
  }

  const payload = (data ?? {}) as Record<string, unknown>;
  const faq = Array.isArray(payload.faq)
    ? payload.faq.flatMap((item) => {
        if (!item || typeof item !== "object") {
          return [];
        }
        const record = item as { question?: unknown; answer?: unknown };
        if (
          typeof record.question !== "string" ||
          typeof record.answer !== "string"
        ) {
          return [];
        }
        return [{ question: record.question, answer: record.answer }];
      })
    : [];

  const detail: AdminServiceDetail = {
    id: String(payload.id ?? ""),
    name: String(payload.name ?? ""),
    slug: String(payload.slug ?? ""),
    categoryId: String(payload.categoryId ?? ""),
    summary: String(payload.summary ?? ""),
    description: String(payload.description ?? ""),
    iconName: typeof payload.iconName === "string" ? payload.iconName : null,
    iconColor: typeof payload.iconColor === "string" ? payload.iconColor : null,
    coverPath: typeof payload.coverPath === "string" ? payload.coverPath : null,
    coverUrl: null,
    ctaType: (payload.ctaType as ServiceCtaType) ?? "quote",
    ctaLabel: String(payload.ctaLabel ?? "Ajánlatot kérek"),
    pricingModel: (payload.pricingModel as ServicePricingModel) ?? "custom_quote",
    priceAmount:
      payload.priceAmount === null || payload.priceAmount === undefined
        ? null
        : asNumber(payload.priceAmount),
    priceUnit: typeof payload.priceUnit === "string" ? payload.priceUnit : null,
    currency: String(payload.currency ?? "HUF"),
    status: (payload.status as ServiceAdminStatus) ?? "draft",
    sortOrder: asNumber(payload.sortOrder),
    isFeatured: Boolean(payload.isFeatured),
    audienceSummary:
      typeof payload.audienceSummary === "string" ? payload.audienceSummary : null,
    problemStatement:
      typeof payload.problemStatement === "string"
        ? payload.problemStatement
        : null,
    benefits: Array.isArray(payload.benefits)
      ? payload.benefits.filter((item): item is string => typeof item === "string")
      : [],
    contactName: typeof payload.contactName === "string" ? payload.contactName : null,
    contactEmail:
      typeof payload.contactEmail === "string" ? payload.contactEmail : null,
    contactPhone:
      typeof payload.contactPhone === "string" ? payload.contactPhone : null,
    faq,
    providerName:
      typeof payload.providerName === "string" ? payload.providerName : null,
    documents: parseAdminDocuments(payload.documents),
    fulfillmentType:
      (payload.fulfillmentType as ServiceFulfillmentType) ?? "none",
    specialistUserId:
      typeof payload.specialistUserId === "string" ? payload.specialistUserId : null,
    specialistName:
      typeof payload.specialistName === "string" ? payload.specialistName : null,
    feedCommunityId:
      typeof payload.feedCommunityId === "string" ? payload.feedCommunityId : null,
    feedCommunityName:
      typeof payload.feedCommunityName === "string"
        ? payload.feedCommunityName
        : null,
    feedAccessDuration:
      (payload.feedAccessDuration as FeedAccessDuration | null) ?? null,
    feedAccessDays:
      payload.feedAccessDays === null || payload.feedAccessDays === undefined
        ? null
        : asNumber(payload.feedAccessDays),
  };

  const mediaUrls = await signServiceMedia([
    detail.coverPath,
    ...detail.documents.map((document) => document.storagePath),
  ]);

  return {
    ...detail,
    coverUrl: detail.coverPath ? mediaUrls.get(detail.coverPath) ?? null : null,
  };
}

function parseAdminDocuments(value: unknown): AdminServiceDocument[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.flatMap((item) => {
    if (!item || typeof item !== "object") {
      return [];
    }
    const record = item as Record<string, unknown>;
    if (typeof record.storagePath !== "string" || !record.storagePath) {
      return [];
    }
    return [
      {
        id: typeof record.id === "string" ? record.id : undefined,
        title: String(record.title ?? record.fileName ?? "dokumentum"),
        fileName: String(record.fileName ?? "dokumentum"),
        mimeType: String(record.mimeType ?? "application/octet-stream"),
        sizeBytes: asNumber(record.sizeBytes),
        storagePath: record.storagePath,
      },
    ];
  });
}

export async function upsertAdminService(input: AdminServiceUpsertInput) {
  const supabase = getSupabaseBrowserClient();
  const { data, error } = await supabase.rpc("upsert_admin_service", {
    payload: {
      id: input.id ?? null,
      name: input.name,
      category_id: input.categoryId,
      summary: input.summary,
      description: input.description,
      icon_name: input.iconName,
      icon_color: input.iconColor,
      cover_path: input.coverPath,
      cta_type: input.ctaType,
      pricing_model: input.pricingModel,
      price_amount: input.priceAmount,
      status: input.status,
      sort_order: input.sortOrder,
      is_featured: input.isFeatured,
      audience_summary: input.audienceSummary,
      problem_statement: input.problemStatement,
      benefits: input.benefits,
      contact_name: input.contactName,
      contact_email: input.contactEmail,
      contact_phone: input.contactPhone,
      faq: input.faq,
      provider_name: input.providerName,
      documents: input.documents.map((document) => ({
        title: document.title,
        file_name: document.fileName,
        mime_type: document.mimeType,
        size_bytes: document.sizeBytes,
        storage_path: document.storagePath,
      })),
    },
  });

  if (error) {
    throw error;
  }

  const serviceId = data as string;
  const { error: unitError } = await supabase.rpc("set_admin_service_price_unit", {
    target_service_id: serviceId,
    next_unit: input.pricingModel === "fixed" ? input.priceUnit : null,
  });

  if (unitError) {
    throw unitError;
  }

  const { error: fulfillmentError } = await supabase.rpc(
    "set_admin_service_fulfillment",
    {
      target_service_id: serviceId,
      next_type: input.fulfillmentType,
      next_specialist_id: input.fulfillmentType === "chat" ? input.specialistUserId : null,
      next_community_id:
        input.fulfillmentType === "feed" ? input.feedCommunityId : null,
      next_feed_duration:
        input.fulfillmentType === "feed" ? input.feedAccessDuration : null,
      next_feed_days:
        input.fulfillmentType === "feed" && input.feedAccessDuration === "days"
          ? input.feedAccessDays
          : null,
    },
  );

  if (fulfillmentError) {
    throw fulfillmentError;
  }

  return serviceId;
}

export async function upsertAdminServiceCategory(
  input: AdminServiceCategoryUpsertInput,
) {
  const supabase = getSupabaseBrowserClient();
  const { error } = await supabase.rpc("upsert_admin_service_category", {
    payload: {
      id: input.id ?? null,
      name: input.name,
      description: input.description,
      is_active: input.isActive,
    },
  });

  if (error) {
    throw error;
  }
}

export async function reorderAdminServiceCategories(orderedIds: string[]) {
  const supabase = getSupabaseBrowserClient();
  const { error } = await supabase.rpc("reorder_admin_service_categories", {
    ordered_ids: orderedIds,
  });

  if (error) {
    throw error;
  }
}

export async function listAdminReports(): Promise<AdminReportRow[]> {
  const supabase = getSupabaseBrowserClient();
  const { data, error } = await supabase.rpc("list_admin_reports");

  if (error) {
    throw error;
  }

  return asRows<{
    id: string;
    target_type: ReportTargetType;
    target_id: string;
    reason: ReportReason;
    details: string | null;
    status: ReportStatus;
    reporter_name: string;
    target_preview: string;
    created_at: string;
    resolved_at: string | null;
    resolution_action: string | null;
  }>(data).map((row) => ({
    id: row.id,
    targetType: row.target_type,
    targetId: row.target_id,
    reason: row.reason,
    details: row.details,
    status: row.status,
    reporterName: row.reporter_name,
    targetPreview: row.target_preview,
    createdAt: row.created_at,
    resolvedAt: row.resolved_at,
    resolutionAction: row.resolution_action,
  }));
}

export async function resolveAdminReport(
  reportId: string,
  action: "dismiss" | "hide" | "suspend_user",
) {
  const supabase = getSupabaseBrowserClient();
  const { error } = await supabase.rpc("resolve_content_report", {
    target_report_id: reportId,
    next_action: action,
  });

  if (error) {
    throw error;
  }
}

export async function createContentReport(input: {
  targetType: ReportTargetType;
  targetId: string;
  reason: ReportReason;
  details?: string;
}) {
  const supabase = getSupabaseBrowserClient();
  const { error } = await supabase.rpc("create_content_report", {
    p_target_type: input.targetType,
    p_target_id: input.targetId,
    p_reason: input.reason,
    p_details: input.details ?? null,
  });

  if (error) {
    throw error;
  }
}
